/**
 * Client-side authenticated fetch utility: the single entry point for API
 * calls from pages and components.
 *
 * - Web: upstream behaviour. Relative URL, credentials: 'include' (session
 *   cookie), JSON Content-Type and an Origin header for CSRF protection.
 * - Native (Capacitor iOS): delegates to `apiFetch` in lib/api.ts, which calls
 *   the absolute API base URL through CapacitorHttp with
 *   `X-Client-Platform: capacitor` and `Authorization: Bearer <session token>`.
 */

import { apiFetch, isNativeApp } from './api'

export {
  apiFetch,
  apiUrl,
  appOrigin,
  clearSession,
  getSessionToken,
  isNativeApp,
  storeSessionTokenFrom,
  streamUrl,
  API_BASE_URL,
  UNAUTHORIZED_EVENT,
} from './api'

export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  if (isNativeApp()) {
    const headers = new Headers(options.headers || {})
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    return apiFetch(url, { ...options, headers })
  }

  // Ensure credentials are included for session-based auth
  const fetchOptions: RequestInit = {
    ...options,
    credentials: 'include', // Always include cookies for session auth
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  }

  // Add Origin header for CSRF protection (if not already set)
  if (!fetchOptions.headers || !('Origin' in fetchOptions.headers)) {
    if (typeof window !== 'undefined') {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        'Origin': window.location.origin,
      }
    }
  }

  return fetch(url, fetchOptions)
}

/**
 * GET request helper
 */
export async function authenticatedGet(url: string, options: RequestInit = {}): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'GET',
  })
}

/**
 * POST request helper
 */
export async function authenticatedPost(
  url: string,
  body?: any,
  options: RequestInit = {}
): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * PATCH request helper
 */
export async function authenticatedPatch(
  url: string,
  body?: any,
  options: RequestInit = {}
): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * PUT request helper
 */
export async function authenticatedPut(
  url: string,
  body?: any,
  options: RequestInit = {}
): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * DELETE request helper
 */
export async function authenticatedDelete(url: string, options: RequestInit = {}): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'DELETE',
  })
}

